import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { BiSearch } from "react-icons/bi";
import { IoMdNotificationsOutline } from "react-icons/io"; 
import { FaBars, FaPlus } from "react-icons/fa6";
import { IoSettingsOutline } from "react-icons/io5";
import { X, Inbox, BookmarkCheck } from "lucide-react";
import Logo from "../assets/images/Logo.png";
import { useSearch } from "../context/SearchContext";
import { useSelector } from "react-redux";
import UserProfile from "../pages/UserProfile";
import { toast } from 'react-toastify';

// API and endpoint helpers
import api from "../common/apiClient"; 
import SummaryApi from "../common/SummaryApi";
import { formatDateTime } from '../helpers/formatDateTime';

// Permission helpers
import { getVisibleKeysForUser, filterSidebarByPermissions } from "../config/SidebarConfig";
import { getNotificationIcon, getNotificationRoute } from '../helpers/notificationHelper'; // Adjust path
const Header = ({
  collapsed,
  toggleSidebar,
  showMobileSidebar,
  activeModule,
  setActiveModule,
  mode,
  setMode,
}) => {
  const { searchQuery, setSearchQuery } = useSearch();
  const [showSearch, setShowSearch] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState(0); 
  
  // States for Sliding Notification Off-canvas Drawer [1]
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const searchRef = useRef(null);
  const quickActionsRef = useRef(null);
  const quickActionsBtnRef = useRef(null);
  const location = useLocation();
  const { portalName } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  // Fetch only unread count on mount to show on badge [1]
  const fetchUnreadCountOnly = useCallback(() => {
    if (user) {
      api.get(SummaryApi.getMyPolicyNotifications.url, { params: { limit: 1 } })
        .then((res) => {
          if (res.data.success) {
            setUnreadNotifications(res.data.unreadCount || 0);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadCountOnly();
  }, [fetchUnreadCountOnly, location.pathname]);

  // Fetch full notification list dynamically when drawer is opened [1]
  const fetchNotificationsForDrawer = async () => {
    setNotificationLoading(true);
    try {
      const res = await api.get(SummaryApi.getMyPolicyNotifications.url, { params: { limit: 50 } });
      if (res.data.success) {
        setNotifications(res.data.data || []);
        setUnreadNotifications(res.data.unreadCount || 0);
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setNotificationLoading(false);
    }
  };

  // Filters modules based on user permissions
  const filteredSidebar = useMemo(() => {
    if (!user) return [];
    const visibleKeysSet = getVisibleKeysForUser(user);
    return filterSidebarByPermissions(visibleKeysSet);
  }, [user]);

  // Gear Icon display logic based on active module settings permissions
  const showSettingsIcon = useMemo(() => {
    if (activeModule) {
      const currentFiltered = filteredSidebar.find(m => m.key === activeModule.key);
      return !!(currentFiltered && currentFiltered.settings && currentFiltered.settings.length > 0);
    }
    return filteredSidebar.some(m => m.settings && m.settings.length > 0);
  }, [filteredSidebar, activeModule]);

  // Quick actions template
  const quickActions = [
    // { name: "New Visit", path: `/${portalName}/sales/leads/create` },
  ];

  // Calculate dropdown position dynamically
  useEffect(() => {
    if (showQuickActions && quickActionsBtnRef.current) {
      const btnRect = quickActionsBtnRef.current.getBoundingClientRect();
      const dropdownWidth = 224;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let position = {
        top: btnRect.bottom + 8,
        maxHeight: windowHeight - btnRect.bottom - 20
      };

      if (btnRect.right - dropdownWidth / 2 + dropdownWidth > windowWidth - 10) {
        position.right = windowWidth - btnRect.right;
      } else if (btnRect.left - dropdownWidth / 2 < 10) {
        position.left = 10;
      } else {
        position.left = btnRect.left - dropdownWidth / 2 + btnRect.width / 2;
      }

      setDropdownPosition(position);
    }
  }, [showQuickActions]);

  useEffect(() => {
    if (id) {
      setSearchQuery(id);
    } else {
      setSearchQuery('');
    }
  }, [location.pathname, setSearchQuery, id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target) &&
          quickActionsBtnRef.current && !quickActionsBtnRef.current.contains(event.target)) {
        setShowQuickActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSettingsActive = mode === "settings";

  const handleSettingsClick = () => {
    if (isSettingsActive) {
      if (activeModule) {
        const hasAccessToActiveModule = filteredSidebar.some(m => m.key === activeModule.key);
        if (hasAccessToActiveModule) {
          const targetPath = activeModule.path || (activeModule.submodules && activeModule.submodules[0]?.path);
          if (targetPath) {
            navigate(`/${portalName}/${targetPath}`);
            return;
          }
        }
      }
      navigate(`/${portalName}/home`);
    } else {
      const current = filteredSidebar.find(m => m.key === activeModule?.key);
      
      if (current && current.settings && current.settings.length > 0) {
        navigate(`/${portalName}/${current.settings[0].path}`);
      } else {
        const fallbackAllowed = filteredSidebar.find(m => m.settings && m.settings.length > 0);
        if (fallbackAllowed) {
          navigate(`/${portalName}/${fallbackAllowed.settings[0].path}`);
        } else {
          navigate(`/${portalName}/home`);
        }
      }
    }
  };

  // FIX: Handles toggling of the sliding drawer [1]
  // If the drawer is already open, clicking the bell icon again closes it cleanly without calling the API [1]
  const handleOpenNotifications = () => {
    if (isNotificationOpen) {
      setIsNotificationOpen(false); // Toggle close [1]
    } else {
      setIsNotificationOpen(true); // Toggle open [1]
      fetchNotificationsForDrawer(); // Only fetch notifications on initial opening trigger [1]
    }
  };

  // Mark a single notification as read inside the drawer [1]
  const handleMarkAsRead = async (notificationId) => {
    try {
      const res = await api.post(SummaryApi.markMyPolicyNotificationRead.url(notificationId));
      if (res.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n))
        );
        setUnreadNotifications((c) => Math.max(0, c - 1));
        toast.success('Marked as read');
      }
    } catch {
      toast.error('Failed to update notification');
    }
  };

  // Handle clicking on an alert item: read it and navigate to its respective detail page [1]
  const handleNotificationItemClick = async (item) => {
      if (!item.isRead) {
        await handleMarkAsRead(item._id);
      }
      setIsNotificationOpen(false); // (For Header sliding drawer only)

      // FIX: Resolves navigation path dynamically from the centralized helper! [1]
      const targetRoute = getNotificationRoute(item, portalName);
      navigate(targetRoute);
    };

  return (
    <div className="bg-white flex items-center justify-between px-4 py-2 relative border-b border-gray-200 shadow-sm z-30">
      {/* Left Section: Logo & Title */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <Link
          to={`/${portalName}/home`}
          onClick={() => {
            setMode("modules");
            setActiveModule(null);
          }}
        >
          <img src={Logo} alt="Logo" className="h-10 w-auto md:h-12 object-cover" />
        </Link>
        {!showSearch && (
          <div className="hidden lg:block lg:ml-32">
            <h2 className="text-lg font-bold text-black md:text-2xl whitespace-nowrap">
              {user?.roleName || 'Admin Dashboard'}
            </h2>
          </div>
        )}
      </div>

      {/* Center Search bar */}
      {showSearch && (
        <div className="flex-1 mx-4 max-w-2xl" ref={searchRef}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search Employee"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 pl-10 pr-10 rounded-md border border-primary bg-white text-sm focus:outline-none focus:ring-[0.3px] focus:ring-primary"
              autoFocus
            />
            <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <IoClose
              onClick={() => {
                setSearchQuery('');
                setShowSearch(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer text-lg hover:text-gray-600"
            />
          </div>
        </div>
      )}

      {/* Right Action Icons Panel */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Quick Actions trigger */}
        <div className="relative hidden md:block">
          <button
            ref={quickActionsBtnRef}
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="bg-primary hover:bg-red-50 hover:text-primary text-white p-2.5 rounded-lg transition-colors"
          >
            <FaPlus className="text-sm" />
          </button>
        </div>

        {!showSearch && (
          <button
            onClick={() => setShowSearch(true)}
            className="text-gray-600 hover:text-primary hover:bg-red-50 p-2.5 rounded-lg transition-colors"
          >
            <BiSearch className="text-xl" />
          </button>
        )}

        {/* Wired up Notification Bell Icon with dynamic active-state styling [1] */}
        <div className="relative">
          <button 
            onClick={handleOpenNotifications} // Triggers sliding drawer [1]
            className={`p-2.5 rounded-lg transition-colors relative duration-200 ${
              isNotificationOpen 
                ? "text-primary bg-red-50" // FIX: Highlights the bell icon in red background when drawer is active [1]
                : "text-textPrimary hover:bg-red-50 hover:text-primary"
            }`}
          >
            <IoMdNotificationsOutline className="text-xl" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold animate-pulse">
                {unreadNotifications}
              </span>
            )}
          </button>
        </div>

        {/* Gear/Settings trigger */}
        {showSettingsIcon && (
          <button
            onClick={handleSettingsClick}
            className={`p-2.5 rounded-lg transition-colors duration-200 ${
              isSettingsActive
                ? "text-primary bg-red-50"
                : "text-textPrimary hover:bg-red-50 hover:text-primary"
            }`}
            title="Settings"
          >
            <IoSettingsOutline size={20} className={`transition-transform duration-300 ${isSettingsActive ? "rotate-90" : ""}`} />
          </button>
        )}

        <div className="flex justify-between items-center">
          <UserProfile user={user} />
        </div>

        {/* Mobile menu trigger */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden text-primary text-xl hover:bg-primaryLight p-2.5 rounded-lg transition-colors"
        >
          {showMobileSidebar ? <X /> : <FaBars />}
        </button>
      </div>

      {/* Quick Actions Dropdown */}
      {showQuickActions && (
        <div
          ref={quickActionsRef}
          className="fixed w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
          style={{
            top: `${dropdownPosition.top}px`,
            ...(dropdownPosition.left && { left: `${dropdownPosition.left}px` }),
            ...(dropdownPosition.right && { right: `${dropdownPosition.right}px` }),
            maxHeight: `${dropdownPosition.maxHeight || 400}px`
          }}
        >
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <span className="text-blue-500">⚙</span> Quick Actions
            </h3>
          </div>
          <div className="py-2 overflow-y-auto" style={{ maxHeight: `${(dropdownPosition.maxHeight || 400) - 100}px` }}>
            <input
              type="text"
              placeholder="Search"
              className="mx-3 mb-2 px-3 py-2 border border-gray-200 rounded-md text-sm w-[calc(100%-24px)] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
                onClick={() => setShowQuickActions(false)}
              >
                {action.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ================================================================================== */}
      {/* 🔔 FIX: COHESIVE NATIVE SLIDING NOTIFICATION DRAWER WITH BLUR & SMOOTH OPACITY [1] */}
      {/* ================================================================================== */}
      <div 
        className={`fixed top-[57px] md:top-[65px] inset-0 z-50 bg-black bg-opacity-30 backdrop-blur-sm transition-opacity duration-300 ${
          isNotificationOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsNotificationOpen(false)} // Close drawer on backdrop click [1]
      >
        <div 
          className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col z-50 transform transition-transform duration-300 ${
            isNotificationOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()} // Prevents closing the drawer when clicking inside
        >
          {/* Drawer Header */}
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IoMdNotificationsOutline className="h-5 w-5 text-primary text-xl" />
              <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
              {unreadNotifications > 0 && (
                <span className="bg-primary text-white text-xs px-2.5 py-0.5 rounded-full font-bold animate-pulse">
                  {unreadNotifications} New
                </span>
              )}
            </div>
            <button 
              type="button"
              onClick={() => setIsNotificationOpen(false)}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Drawer Body (Scrollable alerts list) */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50 p-3 space-y-1">
            {notificationLoading ? (
              <div className="text-center py-12 text-sm text-gray-400">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <Inbox className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <h4 className="text-sm font-semibold text-gray-800">Your Inbox is Clear</h4>
                <p className="text-xs text-gray-400 mt-1">No alerts waiting for your review.</p>
              </div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item._id}
                  onClick={() => handleNotificationItemClick(item)} // Read and Navigate on click [1]
                  className={`flex gap-3 p-3.5 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors ${!item.isRead ? 'bg-red-50/25' : ''}`}
                >
                  {/* Icon Slot */}
                  <div className="w-9 h-9 rounded-lg bg-white border flex items-center justify-center flex-shrink-0 shadow-sm">
                    {getNotificationIcon(item.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5">
                      <h4 className={`text-xs font-bold truncate ${!item.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                        {item.title}
                      </h4>
                      <span className="text-[9px] text-gray-400 whitespace-nowrap">
                        {formatDateTime(item.createdAt, true)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-normal line-clamp-2">
                      {item.message}
                    </p>
                  </div>

                  {/* Quick Mark-as-Read Action button */}
                  {!item.isRead && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // Stop click from triggering parent div's routing [1]
                        handleMarkAsRead(item._id);
                      }}
                      className="p-1.5 rounded-md hover:bg-white text-gray-400 hover:text-primary transition-colors flex-shrink-0 self-center"
                      title="Mark as Read"
                    >
                      <BookmarkCheck className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;

// import React, { useEffect, useState, useRef } from "react";
// import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
// import { IoClose } from 'react-icons/io5';
// import { BiSearch } from "react-icons/bi";
// import { IoMdNotificationsOutline } from "react-icons/io";
// import { FaBars, FaPlus } from "react-icons/fa6";
// import { IoSettingsOutline } from "react-icons/io5";
// import { X } from "lucide-react";
// import Logo from "../assets/images/Logo.png";
// import { useSearch } from "../context/SearchContext";
// import { useSelector } from "react-redux";
// import UserProfile from "../pages/UserProfile";
// import { sidebarConfig } from "../config/SidebarConfig";

// const Header = ({
//   collapsed,
//   toggleSidebar,
//   showMobileSidebar,
//   activeModule,
//   setActiveModule,
//   mode,
//   setMode,
// }) => {
//   const { searchQuery, setSearchQuery } = useSearch();
//   const [showSearch, setShowSearch] = useState(false);
//   const [showQuickActions, setShowQuickActions] = useState(false);
//   const [dropdownPosition, setDropdownPosition] = useState({});
//   const searchRef = useRef(null);
//   const quickActionsRef = useRef(null);
//   const quickActionsBtnRef = useRef(null);
//   const location = useLocation();
//   const { portalName } = useParams();
//   const navigate = useNavigate(); // Navigation-க்காக சேர்க்கப்பட்டது
//   const user = useSelector((state) => state?.user?.user);
//   const queryParams = new URLSearchParams(location.search);
//   const id = queryParams.get("id");

//   // Quick actions list with dynamic portal name
//   const quickActions = [
//     { name: "New Visit", path: `/${portalName}/sales/leads/create` },
//   ];

//   // Calculate dropdown position
//   useEffect(() => {
//     if (showQuickActions && quickActionsBtnRef.current) {
//       const btnRect = quickActionsBtnRef.current.getBoundingClientRect();
//       const dropdownWidth = 224;
//       const windowWidth = window.innerWidth;
//       const windowHeight = window.innerHeight;

//       let position = {
//         top: btnRect.bottom + 8,
//         maxHeight: windowHeight - btnRect.bottom - 20
//       };

//       if (btnRect.right - dropdownWidth / 2 + dropdownWidth > windowWidth - 10) {
//         position.right = windowWidth - btnRect.right;
//       } else if (btnRect.left - dropdownWidth / 2 < 10) {
//         position.left = 10;
//       } else {
//         position.left = btnRect.left - dropdownWidth / 2 + btnRect.width / 2;
//       }

//       setDropdownPosition(position);
//     }
//   }, [showQuickActions]);

//   useEffect(() => {
//     if (id) {
//       setSearchQuery(id);
//     } else {
//       setSearchQuery('');
//     }
//   }, [location.pathname, setSearchQuery, id]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (searchRef.current && !searchRef.current.contains(event.target)) {
//         setShowSearch(false);
//       }
//       if (quickActionsRef.current && !quickActionsRef.current.contains(event.target) &&
//           quickActionsBtnRef.current && !quickActionsBtnRef.current.contains(event.target)) {
//         setShowQuickActions(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const isSettingsActive = mode === "settings";

//   // FIX: செட்டிங்ஸ் கிளிக் செய்யும்போது தானாக முதல் பக்கத்திற்கு ரீ-டைரக்ட் செய்யப்படுகிறது
//   const handleSettingsClick = () => {
//     if (isSettingsActive) {
//       // செட்டிங்ஸை மூடிவிட்டு சாதாரண மாட்யூலுக்குத் திரும்பும்
//       if (activeModule) {
//         const targetPath = activeModule.path || (activeModule.submodules && activeModule.submodules[0]?.path);
//         if (targetPath) {
//           navigate(`/${portalName}/${targetPath}`);
//         } else {
//           navigate(`/${portalName}/home`);
//         }
//       } else {
//         navigate(`/${portalName}/home`);
//       }
//     } else {
//       // செட்டிங்ஸைத் திறந்து, தற்போதைய மாட்யூலின் முதல் செட்டிங்ஸ் பக்கத்திற்கு அழைத்துச் செல்லும்
//       const current = activeModule || sidebarConfig[0];
//       if (current && current.settings && current.settings.length > 0) {
//         navigate(`/${portalName}/${current.settings[0].path}`);
//       } else {
//         navigate(`/${portalName}/home/settings/my-organization`);
//       }
//     }
//   };

//   return (
//     <div className="bg-white flex items-center justify-between px-4 py-2 relative border-b border-gray-200 shadow-sm">
//       {/* Left Section: Logo & Title */}
//       <div className="flex items-center gap-4 flex-shrink-0">
//         <Link
//           to={`/${portalName}/home`}
//           onClick={() => {
//             setMode("modules");
//             setActiveModule(null);
//           }}
//         >
//           <img src={Logo} alt="Logo" className="h-10 w-auto md:h-12 object-cover" />
//         </Link>
//         {!showSearch && (
//           <div className="hidden lg:block lg:ml-32">
//             <h2 className="text-lg font-bold text-black md:text-2xl whitespace-nowrap">
//               {user?.roleName || 'Admin Dashboard'}
//             </h2>
//           </div>
//         )}
//       </div>

//       {/* Center Search - Expands when active */}
//       {showSearch && (
//         <div className="flex-1 mx-4 max-w-2xl" ref={searchRef}>
//           <div className="relative">
//             <input
//               type="text"
//               placeholder="Search Employee"
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               className="w-full py-2 pl-10 pr-10 rounded-md border border-primary bg-white text-sm focus:outline-none focus:ring-[0.3px] focus:ring-primary"
//               autoFocus
//             />
//             <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//             <IoClose
//               onClick={() => {
//                 setSearchQuery('');
//                 setShowSearch(false);
//               }}
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer text-lg hover:text-gray-600"
//             />
//           </div>
//         </div>
//       )}

//       {/* Right Section */}
//       <div className="flex items-center gap-2 flex-shrink-0">
//         {/* Quick Actions */}
//         <div className="relative hidden md:block">
//           <button
//             ref={quickActionsBtnRef}
//             onClick={() => setShowQuickActions(!showQuickActions)}
//             className="bg-primary hover:bg-red-50 hover:text-primary text-white p-2.5 rounded-lg transition-colors"
//           >
//             <FaPlus className="text-sm" />
//           </button>
//         </div>

//         {!showSearch && (
//           <button
//             onClick={() => setShowSearch(true)}
//             className="text-gray-600 hover:text-primary hover:bg-red-50 p-2.5 rounded-lg transition-colors"
//           >
//             <BiSearch className="text-xl" />
//           </button>
//         )}

//         <div className="relative">
//           <button className="text-textPrimary hover:text-primary hover:bg-red-50 p-2.5 rounded-lg transition-colors relative">
//             <IoMdNotificationsOutline className="text-xl" />
//             <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
//               10
//             </span>
//           </button>
//         </div>

//         {/* Settings - Gear icon */}
//         <button
//           onClick={handleSettingsClick}
//           className={`p-2.5 rounded-lg transition-colors duration-200 ${
//             isSettingsActive
//               ? "text-primary bg-red-50"
//               : "text-textPrimary hover:bg-red-50 hover:text-primary"
//           }`}
//           title="Settings"
//         >
//           <IoSettingsOutline size={20} className={`transition-transform duration-300 ${isSettingsActive ? "rotate-90" : ""}`} />
//         </button>

//         <div className="flex justify-between items-center">
//           <UserProfile user={user} />
//         </div>

//         <button
//           onClick={toggleSidebar}
//           className="lg:hidden text-primary text-xl hover:bg-primaryLight p-2.5 rounded-lg transition-colors"
//         >
//           {showMobileSidebar ? <X /> : <FaBars />}
//         </button>
//       </div>

//       {/* Quick Actions Dropdown */}
//       {showQuickActions && (
//         <div
//           ref={quickActionsRef}
//           className="fixed w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
//           style={{
//             top: `${dropdownPosition.top}px`,
//             ...(dropdownPosition.left && { left: `${dropdownPosition.left}px` }),
//             ...(dropdownPosition.right && { right: `${dropdownPosition.right}px` }),
//             maxHeight: `${dropdownPosition.maxHeight || 400}px`
//           }}
//         >
//           <div className="p-3 border-b border-gray-100">
//             <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
//               <span className="text-blue-500">⚙</span> Quick Actions
//             </h3>
//           </div>
//           <div className="py-2 overflow-y-auto" style={{ maxHeight: `${(dropdownPosition.maxHeight || 400) - 100}px` }}>
//             <input
//               type="text"
//               placeholder="Search"
//               className="mx-3 mb-2 px-3 py-2 border border-gray-200 rounded-md text-sm w-[calc(100%-24px)] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
//             />
//             {quickActions.map((action, index) => (
//               <Link
//                 key={index}
//                 to={action.path}
//                 className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
//                 onClick={() => setShowQuickActions(false)}
//               >
//                 {action.name}
//               </Link>
//             ))}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useEffect, useState, useRef, useMemo } from "react";
// import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
// import { IoClose } from 'react-icons/io5';
// import { BiSearch } from "react-icons/bi";
// // import { IoMdNotificationsOutline } from "react-icons/io";
// import { FaBars, FaPlus } from "react-icons/fa6";
// import { IoSettingsOutline } from "react-icons/io5";
// import { X } from "lucide-react";
// import Logo from "../assets/images/Logo.png";
// import { useSearch } from "../context/SearchContext";
// import { useSelector } from "react-redux";
// import UserProfile from "../pages/UserProfile";

// // ──── பர்மிஷன் ஹெல்பர் செயல்பாடுகள் ────
// import { getVisibleKeysForUser, filterSidebarByPermissions } from "../config/SidebarConfig";

// const Header = ({
//   collapsed,
//   toggleSidebar,
//   showMobileSidebar,
//   activeModule,
//   setActiveModule,
//   mode,
//   setMode,
// }) => {
//   const { searchQuery, setSearchQuery } = useSearch();
//   const [showSearch, setShowSearch] = useState(false);
//   const [showQuickActions, setShowQuickActions] = useState(false);
//   const [dropdownPosition, setDropdownPosition] = useState({});
//   const searchRef = useRef(null);
//   const quickActionsRef = useRef(null);
//   const quickActionsBtnRef = useRef(null);
//   const location = useLocation();
//   const { portalName } = useParams();
//   const navigate = useNavigate();
//   const user = useSelector((state) => state?.user?.user);
//   const queryParams = new URLSearchParams(location.search);
//   const id = queryParams.get("id");

//   // பயனரின் அனுமதிக்கு உட்பட்ட மாட்யூல்களை வடிகட்டுதல்
//   const filteredSidebar = useMemo(() => {
//     if (!user) return [];
//     const visibleKeysSet = getVisibleKeysForUser(user);
//     return filterSidebarByPermissions(visibleKeysSet);
//   }, [user]);

//   // ──── 1. செட்டிங்ஸ் ஐகானை காண்பிக்கலாமா என்று முடிவு செய்யும் நிபந்தனை (Dynamic Gear Icon Access Check) ────
//   const showSettingsIcon = useMemo(() => {
//     // பயனர் ஒரு குறிப்பிட்ட மாட்யூலில் (எ.கா: Finance) இருக்கும் போது:
//     if (activeModule) {
//       const currentFiltered = filteredSidebar.find(m => m.key === activeModule.key);
//       // அந்த மாட்யூலின் செட்டிங்ஸ் பக்கங்களில் குறைந்தது ஒன்றிற்காவது அனுமதி உள்ளதா என்று பார்க்கிறோம்
//       return !!(currentFiltered && currentFiltered.settings && currentFiltered.settings.length > 0);
//     }
//     // பயனர் ஹோம் பக்கத்தில் அல்லது பொதுவான பக்கத்தில் இருக்கும் போது:
//     // ஏதேனும் ஒரு மாட்யூலிலாவது அவருக்கு செட்டிங்ஸ் அனுமதி உள்ளதா என்று பார்க்கிறோம்
//     return filteredSidebar.some(m => m.settings && m.settings.length > 0);
//   }, [filteredSidebar, activeModule]);

//   // Quick actions list with dynamic portal name
//   const quickActions = [
//     // { name: "New Visit", path: `/${portalName}/sales/leads/create` },
//   ];

//   // Calculate dropdown position
//   useEffect(() => {
//     if (showQuickActions && quickActionsBtnRef.current) {
//       const btnRect = quickActionsBtnRef.current.getBoundingClientRect();
//       const dropdownWidth = 224;
//       const windowWidth = window.innerWidth;
//       const windowHeight = window.innerHeight;

//       let position = {
//         top: btnRect.bottom + 8,
//         maxHeight: windowHeight - btnRect.bottom - 20
//       };

//       if (btnRect.right - dropdownWidth / 2 + dropdownWidth > windowWidth - 10) {
//         position.right = windowWidth - btnRect.right;
//       } else if (btnRect.left - dropdownWidth / 2 < 10) {
//         position.left = 10;
//       } else {
//         position.left = btnRect.left - dropdownWidth / 2 + btnRect.width / 2;
//       }

//       setDropdownPosition(position);
//     }
//   }, [showQuickActions]);

//   useEffect(() => {
//     if (id) {
//       setSearchQuery(id);
//     } else {
//       setSearchQuery('');
//     }
//   }, [location.pathname, setSearchQuery, id]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (searchRef.current && !searchRef.current.contains(event.target)) {
//         setShowSearch(false);
//       }
//       if (quickActionsRef.current && !quickActionsRef.current.contains(event.target) &&
//           quickActionsBtnRef.current && !quickActionsBtnRef.current.contains(event.target)) {
//         setShowQuickActions(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const isSettingsActive = mode === "settings";

//   // செகூர் செய்யப்பட்ட செட்டிங்ஸ் கிளிக் லாஜிக்
//   const handleSettingsClick = () => {
//     if (isSettingsActive) {
//       if (activeModule) {
//         const hasAccessToActiveModule = filteredSidebar.some(m => m.key === activeModule.key);
//         if (hasAccessToActiveModule) {
//           const targetPath = activeModule.path || (activeModule.submodules && activeModule.submodules[0]?.path);
//           if (targetPath) {
//             navigate(`/${portalName}/${targetPath}`);
//             return;
//           }
//         }
//       }
//       navigate(`/${portalName}/home`);
//     } else {
//       const current = filteredSidebar.find(m => m.key === activeModule?.key);
      
//       if (current && current.settings && current.settings.length > 0) {
//         navigate(`/${portalName}/${current.settings[0].path}`);
//       } else {
//         const fallbackAllowed = filteredSidebar.find(m => m.settings && m.settings.length > 0);
//         if (fallbackAllowed) {
//           navigate(`/${portalName}/${fallbackAllowed.settings[0].path}`);
//         } else {
//           navigate(`/${portalName}/home`);
//         }
//       }
//     }
//   };

//   return (
//     <div className="bg-white flex items-center justify-between px-4 py-2 relative border-b border-gray-200 shadow-sm z-30">
//       {/* Left Section: Logo & Title */}
//       <div className="flex items-center gap-4 flex-shrink-0">
//         <Link
//           to={`/${portalName}/home`}
//           onClick={() => {
//             setMode("modules");
//             setActiveModule(null);
//           }}
//         >
//           <img src={Logo} alt="Logo" className="h-10 w-auto md:h-12 object-cover" />
//         </Link>
//         {!showSearch && (
//           <div className="hidden lg:block lg:ml-32">
//             <h2 className="text-lg font-bold text-black md:text-2xl whitespace-nowrap">
//               {user?.roleName || 'Admin Dashboard'}
//             </h2>
//           </div>
//         )}
//       </div>

//       {/* Center Search - Expands when active */}
//       {showSearch && (
//         <div className="flex-1 mx-4 max-w-2xl" ref={searchRef}>
//           <div className="relative">
//             <input
//               type="text"
//               placeholder="Search Employee"
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               className="w-full py-2 pl-10 pr-10 rounded-md border border-primary bg-white text-sm focus:outline-none focus:ring-[0.3px] focus:ring-primary"
//               autoFocus
//             />
//             <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//             <IoClose
//               onClick={() => {
//                 setSearchQuery('');
//                 setShowSearch(false);
//               }}
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer text-lg hover:text-gray-600"
//             />
//           </div>
//         </div>
//       )}

//       {/* Right Section */}
//       <div className="flex items-center gap-2 flex-shrink-0">
//         {/* Quick Actions */}
//         <div className="relative hidden md:block">
//           <button
//             ref={quickActionsBtnRef}
//             onClick={() => setShowQuickActions(!showQuickActions)}
//             className="bg-primary hover:bg-red-50 hover:text-primary text-white p-2.5 rounded-lg transition-colors"
//           >
//             <FaPlus className="text-sm" />
//           </button>
//         </div>

//         {!showSearch && (
//           <button
//             onClick={() => setShowSearch(true)}
//             className="text-gray-600 hover:text-primary hover:bg-red-50 p-2.5 rounded-lg transition-colors"
//           >
//             <BiSearch className="text-xl" />
//           </button>
//         )}

//         {/* <div className="relative">
//           <button className="text-textPrimary hover:text-primary hover:bg-red-50 p-2.5 rounded-lg transition-colors relative">
//             <IoMdNotificationsOutline className="text-xl" />
//             <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
//               10
//             </span>
//           </button>
//         </div> */}

//         {/* ──── 2. செட்டிங்ஸ் ஐகான் காட்டும் பகுதி (Conditional Rendering of Gear Icon based on Permission) ──── */}
//         {showSettingsIcon && (
//           <button
//             onClick={handleSettingsClick}
//             className={`p-2.5 rounded-lg transition-colors duration-200 ${
//               isSettingsActive
//                 ? "text-primary bg-red-50"
//                 : "text-textPrimary hover:bg-red-50 hover:text-primary"
//             }`}
//             title="Settings"
//           >
//             <IoSettingsOutline size={20} className={`transition-transform duration-300 ${isSettingsActive ? "rotate-90" : ""}`} />
//           </button>
//         )}

//         <div className="flex justify-between items-center">
//           <UserProfile user={user} />
//         </div>

//         <button
//           onClick={toggleSidebar}
//           className="lg:hidden text-primary text-xl hover:bg-primaryLight p-2.5 rounded-lg transition-colors"
//         >
//           {showMobileSidebar ? <X /> : <FaBars />}
//         </button>
//       </div>

//       {/* Quick Actions Dropdown */}
//       {showQuickActions && (
//         <div
//           ref={quickActionsRef}
//           className="fixed w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
//           style={{
//             top: `${dropdownPosition.top}px`,
//             ...(dropdownPosition.left && { left: `${dropdownPosition.left}px` }),
//             ...(dropdownPosition.right && { right: `${dropdownPosition.right}px` }),
//             maxHeight: `${dropdownPosition.maxHeight || 400}px`
//           }}
//         >
//           <div className="p-3 border-b border-gray-100">
//             <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
//               <span className="text-blue-500">⚙</span> Quick Actions
//             </h3>
//           </div>
//           <div className="py-2 overflow-y-auto" style={{ maxHeight: `${(dropdownPosition.maxHeight || 400) - 100}px` }}>
//             <input
//               type="text"
//               placeholder="Search"
//               className="mx-3 mb-2 px-3 py-2 border border-gray-200 rounded-md text-sm w-[calc(100%-24px)] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
//             />
//             {quickActions.map((action, index) => (
//               <Link
//                 key={index}
//                 to={action.path}
//                 className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
//                 onClick={() => setShowQuickActions(false)}
//               >
//                 {action.name}
//               </Link>
//             ))}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;